/**
 * import.js - Import API service.
 *
 * Handles file upload preview, import execution, import history,
 * watched-folder management, Dynamics 365 live connection, and
 * the column-name guide.
 *
 * All functions return Promises and are designed to be called from
 * the Import page component.
 */

import axios from "axios";
import { API_BASE } from "../config.js";
import { getAccessToken } from "../auth/apiAuth.jsx";

const api = axios.create({
  baseURL: `${API_BASE}/api/import`,
  timeout: 120000, // longer timeout for pull operations
});

api.interceptors.request.use((config) => {
  const tk = getAccessToken && getAccessToken();
  if (tk) config.headers.Authorization = `Bearer ${tk}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      let msg =
        data?.error?.message ||
        (typeof data?.error === "string" ? data.error : null) ||
        data?.detail ||
        data?.message ||
        "Unknown error";
      const e = new Error(msg);
      e.status = status;
      e.details = data;
      if (status === 401) e.message = "Your session has expired. Please sign in again.";
      return Promise.reject(e);
    }
    return Promise.reject(error);
  }
);

/* ------------------------------------------------------------------ */
/* File upload & preview                                               */
/* ------------------------------------------------------------------ */

/**
 * Preview an import file to detect entity type, column mapping, and row count.
 */
export async function previewImport(file, dataType) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("dataType", dataType);

  const response = await api.post("/preview", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

/**
 * Execute an import from an uploaded file.
 */
export async function runImport(file, dataType) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("dataType", dataType);

  const response = await api.post("/run", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

/* ------------------------------------------------------------------ */
/* Import history                                                      */
/* ------------------------------------------------------------------ */

export async function getImportHistory() {
  const response = await api.get("/history");
  return response.data || [];
}

/* ------------------------------------------------------------------ */
/* Watched folder                                                      */
/* ------------------------------------------------------------------ */

export async function getWatchedFolderStatus() {
  const response = await api.get("/watched-folder");
  return response.data;
}

export async function scanWatchedFolder() {
  const response = await api.post("/watched-folder/scan");
  return response.data;
}

/* ------------------------------------------------------------------ */
/* Dynamics 365 live connection                                        */
/* ------------------------------------------------------------------ */

export async function getDynamicsConnectionStatus() {
  const response = await api.get("/dynamics/status");
  return response.data;
}

export async function testDynamicsConnection() {
  const response = await api.post("/dynamics/test");
  return response.data;
}

export async function pullFromDynamics() {
  const response = await api.post("/dynamics/pull");
  return response.data;
}

/* ------------------------------------------------------------------ */
/* Column name guide                                                   */
/* ------------------------------------------------------------------ */

export async function getColumnGuide() {
  const response = await api.get("/column-guide");
  return response.data;
}
